import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Banner from "../components/Banner/Banner";
import About from "../components/About/About";
import { Container } from "../components/Grid";
import API from "../utils/API";
import bookPile from "../images/Book-Pile.png";
import "./Home.css";

const emptyCredentials = { email: "", password: "" };

function validate(credentials) {
  const errors = {};
  if (!credentials.email.trim()) errors.email = "Enter your email address.";
  else if (!/^\S+@\S+\.\S+$/.test(credentials.email)) errors.email = "Enter a valid email address.";
  if (!credentials.password) errors.password = "Enter your password.";
  else if (credentials.password.length < 6) errors.password = "Password must be at least 6 characters.";
  return errors;
}

export default function Home() {
  const history = useHistory();
  const [login, setLogin] = useState(emptyCredentials);
  const [signup, setSignup] = useState(emptyCredentials);
  const [loginErrors, setLoginErrors] = useState({});
  const [signupErrors, setSignupErrors] = useState({});
  const [loading, setLoading] = useState("");
  const [requestError, setRequestError] = useState("");
  const [showSignup, setShowSignup] = useState(false);

  const updateCredentials = (setter) => (event) => {
    const { name, value } = event.target;
    setter((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event, credentials, request, action, setFormErrors) => {
    event.preventDefault();
    const nextErrors = validate(credentials);
    setFormErrors(nextErrors);
    setRequestError("");
    if (Object.keys(nextErrors).length) return;

    setLoading(action);
    try {
      const response = await request(credentials);
      if (!response.ok) throw new Error("Request failed");
      const payload = await response.json();
      const user = payload.user || payload;
      if (!user || !user._id) throw new Error("Missing user id");
      if (payload.token) localStorage.setItem("onceUponAdventureToken", payload.token);
      history.push("/user/" + user._id);
    } catch (error) {
      setRequestError(action === "signup" ? "We could not create your account. Please try again." : "We could not log you in. Check your details and try again.");
    } finally {
      setLoading("");
    }
  };

  return (
    <Container>
      <main className="home-page">
        <div className="home-hero row align-items-center">
          <div className="col-lg-7 home-story-column">
            <div className="home-kicker"><span aria-hidden="true">✦</span> A storybook learning realm</div>
            <h1 className="home-title"><span>Once</span><span>Upon</span><span>an</span><span className="home-title-last">Adventure</span></h1>
            <p className="home-intro">Your next chapter begins here.</p>
            <div className="home-banner-frame">
              <Banner />
              <span className="home-spark home-spark-one" aria-hidden="true">✦</span>
              <span className="home-spark home-spark-two" aria-hidden="true">✧</span>
            </div>
            <p className="home-story-note">Choose your path, meet curious characters, and discover a little magic along the way.</p>
          </div>

          <div className="col-lg-5 home-auth-column">
            <section className="auth-card" aria-labelledby="welcome-title">
              <div className="auth-card__ornament" aria-hidden="true">✦</div>
              <p className="auth-eyebrow">{showSignup ? "Begin a new chapter" : "The gates are open"}</p>
              <h2 id="welcome-title">{showSignup ? "Create your account" : "Welcome, traveler"}</h2>
              <p className="auth-subtitle">{showSignup ? "Make a place for your story to begin." : "Sign in to continue your adventure."}</p>
              {requestError && <p className="auth-alert" role="alert">{requestError}</p>}
              {!showSignup ? (
                <form className="login auth-form" aria-label="Log in" noValidate onSubmit={(event) => submit(event, login, API.findUser, "login", setLoginErrors)}>
                  <div className="field-group">
                    <label htmlFor="login-email">Email address</label>
                    <input name="email" value={login.email} type="email" onChange={updateCredentials(setLogin)} className="form-control special" id="login-email" autoComplete="email" required aria-describedby={loginErrors.email ? "login-email-error" : undefined} placeholder="you@example.com" />
                    {loginErrors.email && <p id="login-email-error" className="field-error" role="alert">{loginErrors.email}</p>}
                  </div>
                  <div className="field-group">
                    <label htmlFor="login-password">Password</label>
                    <input name="password" value={login.password} type="password" onChange={updateCredentials(setLogin)} className="form-control special" id="login-password" autoComplete="current-password" required aria-describedby={loginErrors.password ? "login-password-error" : undefined} placeholder="Your secret word" />
                    {loginErrors.password && <p id="login-password-error" className="field-error" role="alert">{loginErrors.password}</p>}
                  </div>
                  <button className="btn auth-primary" type="submit" disabled={loading === "login"}>{loading === "login" ? "Opening the gates…" : "Enter the Realm"}</button>
                </form>
              ) : (
                <form id="signup-panel" className="signup auth-form" aria-label="Create an account" noValidate onSubmit={(event) => submit(event, signup, API.saveUser, "signup", setSignupErrors)}>
                  <div className="field-group">
                    <label htmlFor="signup-email">Email address</label>
                    <input name="email" value={signup.email} type="email" onChange={updateCredentials(setSignup)} className="form-control special" id="signup-email" autoComplete="email" required aria-describedby={signupErrors.email ? "signup-email-error" : undefined} placeholder="you@example.com" />
                    {signupErrors.email && <p id="signup-email-error" className="field-error" role="alert">{signupErrors.email}</p>}
                  </div>
                  <div className="field-group">
                    <label htmlFor="signup-password">Password</label>
                    <input name="password" value={signup.password} type="password" onChange={updateCredentials(setSignup)} className="form-control special" id="signup-password" autoComplete="new-password" required aria-describedby={signupErrors.password ? "signup-password-error" : undefined} placeholder="At least 6 characters" />
                    {signupErrors.password && <p id="signup-password-error" className="field-error" role="alert">{signupErrors.password}</p>}
                  </div>
                  <button type="submit" className="btn auth-primary" disabled={loading === "signup"}>{loading === "signup" ? "Writing your story…" : "Begin My Adventure"}</button>
                </form>
              )}
              <div className="auth-divider"><span>{showSignup ? "already have an account?" : "or"}</span></div>
              <button type="button" className="btn auth-secondary" aria-expanded={showSignup} aria-controls="signup-panel" onClick={() => { setShowSignup((visible) => !visible); setRequestError(""); }}>{showSignup ? "Return to Sign In" : "Create an Account"}</button>
              <p className="auth-footnote">{showSignup ? "Your next chapter is only a step away." : "New tales await beyond the castle gates."}</p>
              <img className="auth-book" src={bookPile} alt="A pile of adventure books" />
            </section>
          </div>
        </div>
        <div className="home-about"><About /></div>
      </main>
    </Container>
  );
}
